import { useAppSelector } from '@/hooks';
import {
  selectCurrentSubscription,
  selectUsageStatistics,
} from '@/features/subscriptions/subscriptionsSlice';

/**
 * Hook to check subscription limits for a specific resource
 *
 * @param {string} resourceKey - The usage stats key: 'users', 'campuses', 'dashboards',
 *   'metrics' or 'data_entries'
 * @returns {Object} Limit information and helper methods
 *
 * @example
 * const { isAtLimit, remaining, limit } = useSubscriptionLimit('campuses');
 *
 * <Button disabled={isAtLimit}>Add campus</Button>
 * {isAtLimit && <p>Campus limit reached ({limit})</p>}
 */
export function useSubscriptionLimit(resourceKey) {
  const subscription = useAppSelector(selectCurrentSubscription);
  const usageStats = useAppSelector(selectUsageStatistics);

  // Get the usage stat for this resource
  const stat = usageStats?.[resourceKey];
  const limit = stat?.limit;
  const current = stat?.current ?? 0;
  // A monthly allowance (data entries) rather than a standing count.
  const isPerMonth = stat?.per_month === true;

  // Determine limit status
  const isUnlimited = limit === null || limit === undefined;
  const isAtLimit = !isUnlimited && current >= limit;
  const isApproachingLimit = !isUnlimited && !isAtLimit && current >= limit * 0.8;
  const remaining = isUnlimited ? Infinity : Math.max(0, limit - current);
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);

  // Get plan name for upgrade messaging
  const planName = subscription?.plan?.display_name || 'current plan';

  return {
    // Current values
    current,
    limit,
    remaining,
    percentage,

    // Status flags
    isUnlimited,
    isAtLimit,
    isApproachingLimit,
    isPerMonth,

    // Loading state (no subscription data yet)
    isLoading: !subscription,

    // Helper for messaging
    planName,

    // Formatted limit display
    limitDisplay: isUnlimited ? 'Unlimited' : limit,
  };
}

export default useSubscriptionLimit;
